import React, { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";

import AvatarUploadButton from "../management/AvatarUploadButton";
import firebase from "../../firebase";
import { makeid } from "../../utility";

export default function NewGroupDialog(props) {
    const [group, setGroup] = useState({ key: makeid(19) });
    const [inProgress, setInProgress] = useState(false);

    const handleClose = () => {
        if (!inProgress) {
            props.onClose();
        }
    };

    const handleTextChange = (event) => {
        setGroup((previousGroup) => ({
            ...previousGroup,
            title: event.target.value,
        }));
    };

    async function handleAddClick() {
        if (!group.title || inProgress) {
            return;
        }

        setInProgress(true);

        try {
            const downloadURL = await uploadAvatar();
            props.createGroup(group, downloadURL);
        } catch (error) {
            console.log(error);
        } finally {
            setInProgress(false);
        }
    }

    function uploadAvatar() {
        return new Promise((resolve, reject) => {
            let storage = firebase.storage();

            if (group.avatarEditor) {
                group.avatarEditor
                    .getImageScaledToCanvas()
                    .toBlob(function (blob) {
                        let avatarRef = storage
                            .ref()
                            .child(
                                "avatars/conversations/" +
                                    group.key +
                                    "/avatar.png",
                            );

                        avatarRef
                            .put(blob)
                            .then((snapshot) => {
                                console.log("Upload Successful");
                                group.avatar = null;

                                avatarRef.getDownloadURL().then((url) => {
                                    resolve(url);
                                });
                            })
                            .catch((error) => {
                                let errorCode = error.code;
                                let errorMessage = error.message;
                                console.log(errorCode + ":" + errorMessage);
                                reject(error);
                            });
                    });
            } else {
                resolve("");
            }
        });
    }

    return (
        <Dialog
            onClose={handleClose}
            open={props.open}
            fullWidth
            maxWidth="xs"
            aria-labelledby="new-group-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <GroupAddOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="new-group-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Create a group
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Add a name and an optional image for this conversation.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close group setup"
                    onClick={handleClose}
                    disabled={inProgress}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2.25,
                        p: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2.5,
                        backgroundColor: "background.paper",
                    }}
                >
                    <AvatarUploadButton
                        recipient={group}
                        path="conversations"
                    />
                    <TextField
                        id="group-name"
                        label="Group name"
                        variant="outlined"
                        value={group.title || ""}
                        onChange={handleTextChange}
                        autoFocus
                        fullWidth
                        inputProps={{ maxLength: 80 }}
                        onKeyDown={(event) => {
                            if (
                                event.key === "Enter" &&
                                group.title &&
                                !inProgress
                            ) {
                                event.preventDefault();
                                handleAddClick();
                            }
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": { borderRadius: 2 },
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                <Button
                    color="inherit"
                    onClick={handleClose}
                    disabled={inProgress}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleAddClick}
                    disabled={!group.title || inProgress}
                >
                    {inProgress ? "Creating…" : "Create group"}
                </Button>
            </DialogActions>
            {inProgress && <LinearProgress />}
        </Dialog>
    );
}
